import { CheckOutlined, InfoCircleOutlined, PlusOutlined, ReadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { CriterionProfile } from '@shared/views';
import type { RotationMatch } from '@shared/types';
import { api } from '../api/client.js';
import {
  addCriterion,
  evaluate,
  looksUnanswerable,
  PRESETS,
  recordRound,
  removeCriterion,
  summariseRow,
  SYNTHETIC_RULES,
  useCriteria,
  type CandidateFacts,
  type CellResult,
  type ComputedSpec,
  type Criterion,
} from '../lib/rotationCriteria.js';
import {
  durationTH,
  leafUnit,
  propose,
  removeFromDraft,
  shortlistOf,
  unpropose,
  type DraftItem,
} from '../lib/rotationDraft.js';

/**
 * กระดานเทียบผู้ได้รับการพิจารณาของอัตราหนึ่ง
 *
 * ────────────────────────────────────────────────────────────────
 *  สิ่งที่หน้านี้เป็น และสิ่งที่หน้านี้ไม่เป็น
 * ────────────────────────────────────────────────────────────────
 *
 * เป็น — ตารางที่ตอบว่า "ใครตรงกับเกณฑ์ที่ท่านตั้งไว้" ทีละเกณฑ์ ทีละคน
 *        และผู้ช่วยที่อ่านประวัติของแต่ละท่านให้ฟัง พร้อมบอกว่าประวัติต่างกันตรงไหน
 * ไม่เป็น — การประเมินว่าใครเหมาะ การจัดลำดับ หรือการให้คะแนน
 *
 * เกณฑ์เป็นของผู้ใช้ ดุลพินิจจึงยังเป็นของผู้ใช้ ระบบแค่ตรวจตามที่สั่งและอ่านให้ฟัง
 * เหตุผลเต็มอยู่ที่หัวไฟล์ lib/rotationCriteria.ts และ server/services/criterionCheck.ts
 *
 * ⚠ ช่องสรุปขวาสุดต้องเป็นคำ ห้ามเป็นตัวเลข
 *   "3 จาก 4" อ่านเป็นคะแนนความเหมาะสมทันที แล้วหน้านี้จะกลายเป็นการจัดอันดับ
 *
 * ⚠ ขีด (—) ไม่เท่ากับ ✕
 *   unknown แปลว่าไม่มีข้อมูลให้ตอบ ไม่ได้แปลว่าท่านนั้นไม่เข้าเกณฑ์
 *   สองอย่างนี้ต้องหน้าตาต่างกันเสมอ ไม่งั้นเท่ากับตัดสินคนด้วยช่องว่างในฐานข้อมูล
 *
 * ⚠ การ์ดของผู้ช่วยต้องเรียงตามลำดับในบัญชี ห้ามเรียงใหม่ด้วยเหตุผลใดทั้งสิ้น
 *   ลำดับของการ์ดบนหน้าจอ ถูกอ่านเป็นลำดับความเหมาะสมเสมอ ไม่ว่าจะเขียนกำกับไว้อย่างไร
 */
export function CandidateBoard({
  match,
  matches,
  draft,
  onBack,
}: {
  match: RotationMatch;
  /** ทั้งรอบ — ใช้นับว่าท่านนี้อยู่ในข่ายของอัตราอื่นกี่อัตรา */
  matches: RotationMatch[];
  draft: DraftItem[];
  onBack: () => void;
}) {
  const posNo = match.posNo;
  const board = useCriteria(posNo);
  const [adding, setAdding] = useState(false);
  const [showRounds, setShowRounds] = useState(false);
  const [conflict, setConflict] = useState<DraftItem | null>(null);
  /** ผลจากผู้ช่วยรอบล่าสุด — key คือ `${ชื่อ} ${รหัสเกณฑ์}` */
  const [aiCells, setAiCells] = useState<Record<string, CellResult>>({});
  const [aiProfiles, setAiProfiles] = useState<CriterionProfile[]>([]);
  const [aiContrasts, setAiContrasts] = useState<string[]>([]);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiRan, setAiRan] = useState(false);

  const people = useMemo(() => shortlistOf(draft, posNo), [draft, posNo]);

  /**
   * ข้อเท็จจริงรายคน — ประกอบจาก RotationCandidate ของอัตรานี้
   *
   * ⚠ คนที่ถูกใส่มาจาก "เลือกคนอื่นจากทั้งกรม" จะไม่อยู่ใน match.candidates
   *   ค่าที่หาไม่เจอต้องเป็น null แล้วตกไปเป็น "ไม่มีข้อมูล" — ห้ามเดาค่าแทน
   */
  const facts = useMemo(() => {
    const out = new Map<string, CandidateFacts>();
    for (const it of people) {
      const c = match.candidates.find((x) =>
        it.person.personaId ? x.personaId === it.person.personaId : x.name === it.person.name,
      );
      const others = it.person.personaId
        ? matches.filter(
            (m) =>
              m.posNo !== match.posNo &&
              m.candidates.some((x) => x.personaId === it.person.personaId),
          ).length
        : 0;
      out.set(it.id, {
        key: it.id,
        name: it.person.name,
        monthsInPosition: c?.monthsInPosition ?? it.person.monthsInPosition,
        fromUnitRatio: c?.fromUnitRatio ?? 0,
        toUnitRatio: match.toUnitRatio,
        retireInYears: c?.retireInYears ?? it.person.retireInYears,
        otherVacancies: others,
      });
    }
    return out;
  }, [people, match, matches]);

  const assistantCriteria = board.criteria.filter((c) => c.kind === 'assistant');

  /**
   * ผลของทุกช่อง — เกณฑ์ที่คำนวณได้ตอบทันที เกณฑ์เชิงเนื้อความมาจากคำตอบของผู้ช่วย
   *
   * ⚠ ต้องเป็นฟังก์ชันที่รับ aiCells เข้าไป ไม่ใช่ปิดทับตัวแปรใน useMemo อย่างเดียว
   *   เพราะตอนบันทึกรอบ เราต้องประกอบผลจากคำตอบที่ **เพิ่งได้** ไม่ใช่ค่าใน state
   *   ซึ่งยังเป็นของรอบก่อน — เคยพลาดมาแล้ว ผลคือทุกรอบถูกบันทึกช้าไปหนึ่งก้าว
   *   แล้วแถบ "เปลี่ยนไปกี่ท่าน" รายงานผิดทุกครั้ง
   */
  const buildGrid = (cells: Record<string, CellResult>): Record<string, Record<string, CellResult>> => {
    const out: Record<string, Record<string, CellResult>> = {};
    for (const it of people) {
      const row: Record<string, CellResult> = {};
      const f = facts.get(it.id);
      for (const c of board.criteria) {
        if (c.kind === 'computed' && c.spec && f) {
          row[c.id] = evaluate(c.spec, f);
        } else {
          row[c.id] = cells[`${it.person.name} ${c.id}`] ?? {
            verdict: 'unknown',
            detail: 'ยังไม่ได้ให้ผู้ช่วยอ่าน',
          };
        }
      }
      out[it.person.name] = row;
    }
    return out;
  };

  const grid = useMemo(
    () => buildGrid(aiCells),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [people, board.criteria, facts, aiCells],
  );

  /** การ์ดของผู้ช่วย เรียงตามลำดับในบัญชี ไม่ใช่ตามลำดับที่ผู้ช่วยส่งกลับมา */
  const profileOf = useMemo(() => {
    const m = new Map<string, CriterionProfile>();
    for (const p of aiProfiles) m.set(p.name, p);
    return m;
  }, [aiProfiles]);

  const run = useMutation({
    mutationFn: () =>
      api.rotationCriteria({
        vacancy: {
          title: match.title,
          unit: match.toUnit,
          posNo: match.posNo,
          vacantMonths: match.vacantMonths,
        },
        criteria: assistantCriteria.map((c) => ({ id: c.id, label: c.label })),
        people: people.map((it) => ({
          personaId: it.person.personaId,
          name: it.person.name,
          fromTitle: it.person.title,
          fromUnit: leafUnit(it.person.unit),
          fromMonths: it.person.monthsInPosition,
        })),
      }),
    onSuccess: (res) => {
      const next: Record<string, CellResult> = {};
      for (const c of res.cells) {
        next[`${c.name} ${c.criterionId}`] = { verdict: c.verdict, detail: c.detail };
      }
      setAiCells(next);
      setAiProfiles(res.profiles);
      setAiContrasts(res.contrasts);
      setAiWarnings(res.warnings);
      setAiRan(true);
      if (posNo) recordRound(posNo, board.criteria, buildGrid(next));
    },
  });

  if (!posNo) {
    return (
      <div className="simmain rd-panel">
        <p className="pmodal-note">
          อัตรานี้ไม่มีเลขที่ตำแหน่งในข้อมูล จึงตั้งบัญชีผู้ได้รับการพิจารณาไม่ได้
        </p>
        <button type="button" className="rd-ghost" onClick={onBack}>
          กลับไปเลือกต่อ
        </button>
      </div>
    );
  }

  const lastRound = board.rounds[board.rounds.length - 1];
  /** กี่ท่านที่สรุปตามเกณฑ์เปลี่ยนไปจากรอบก่อน — เทียบจากผลที่บันทึกไว้ ไม่ใช่จากผลบนจอ */
  const changedCount = (() => {
    if (board.rounds.length < 2) return 0;
    const prev = board.rounds[board.rounds.length - 2]!;
    const cur = board.rounds[board.rounds.length - 1]!;
    let n = 0;
    for (const name of Object.keys(cur.results)) {
      const a = summariseRow(cur.criteria, cur.results[name]);
      const b = summariseRow(prev.criteria, prev.results[name]);
      if (a !== b) n += 1;
    }
    return n;
  })();

  return (
    <div className="simmain rd-panel">
      <section className="pmodal-sec">
        {/* ⚠ หัวของหน้านี้คือ "อัตราที่กำลังพิจารณา" ไม่ใช่ป้ายกำกับส่วน
            แถบบนของโมดัลเป็นของทั้งรอบโยกย้าย (687 อัตรา) ซึ่งไม่ได้บอกว่า
            ตอนนี้กำลังทำอัตราไหนอยู่ ถ้าไม่เขียนตรงนี้ ผู้ใช้จะไม่มีที่ยืนยันเลย */}
        <div className="cbtop">
          <span className="cb-kicker">บัญชีผู้ได้รับการพิจารณา · {people.length} ท่าน</span>
          <b>
            {match.title} · {leafUnit(match.toUnit)}
          </b>
          <span className="cb-sub">
            เลขที่ {posNo}
            {match.vacantMonths !== null && ` · ว่างมาแล้ว ${durationTH(match.vacantMonths)}`}
          </span>
        </div>

        {/* ---------- ๑ เกณฑ์ที่ผู้ใช้ตั้ง ---------- */}
        <div className="cbcrit">
          <div className="cb-hd">
            <b>เกณฑ์ที่ท่านตั้งไว้</b>
            <span>ระบบตรวจตามเกณฑ์นี้ ไม่ได้ตั้งเกณฑ์เอง</span>
          </div>
          <div className="cb-chips">
            {board.criteria.map((c) => (
              <span key={c.id} className={c.kind === 'assistant' ? 'cb-chip is-ai' : 'cb-chip'}>
                <b>{c.id}</b> {c.label}
                <em>{c.kind === 'assistant' ? 'ผู้ช่วยอ่านประวัติ' : 'ตรวจด้วยข้อมูล'}</em>
                <button
                  type="button"
                  onClick={() => removeCriterion(posNo, c.id)}
                  aria-label={`เอาเกณฑ์ ${c.id} ออก`}
                >
                  ✕
                </button>
              </span>
            ))}
            <button type="button" className="cb-add" onClick={() => setAdding(true)}>
              <PlusOutlined aria-hidden="true" /> เพิ่มเกณฑ์
            </button>
          </div>
        </div>

        {conflict && (
          <p className="state error sm">
            {conflict.person.name} ถูกเสนอไว้แล้วที่ตำแหน่ง{conflict.target.title}{' '}
            {leafUnit(conflict.target.unit)} — ต้องถอนธงที่นั่นก่อน คนหนึ่งคนเสนอได้อัตราเดียว
          </p>
        )}

        {/* ---------- ๒ ตารางตามเกณฑ์ ---------- */}
        {people.length === 0 ? (
          <p className="pmodal-note">
            ยังไม่มีใครในบัญชี — กลับไปกด &ldquo;ใส่บัญชีพิจารณา&rdquo; ที่ผู้อยู่ในข่าย ใส่ได้หลายท่าน
          </p>
        ) : board.criteria.length === 0 ? (
          <p className="pmodal-note">
            ยังไม่ได้ตั้งเกณฑ์ — กด &ldquo;เพิ่มเกณฑ์&rdquo; ด้านบนเพื่อให้ระบบกาช่องให้
            หรือข้ามไปให้ผู้ช่วยอ่านประวัติด้านล่างได้เลย
          </p>
        ) : (
          <>
            {/* ⚠ ต้องมีบรรทัดบอกว่าตารางที่เห็นอยู่คำนวณมาจากอะไร
                ไม่งั้นผู้ใช้แยกไม่ออกว่าช่องไหนระบบคิดเอง ช่องไหนผู้ช่วยตอบ */}
            <div className="cbnow">
              <b>เกณฑ์ {board.criteria.length} ข้อ</b>
              <span>
                {assistantCriteria.length === 0
                  ? 'ทั้งหมดตรวจด้วยข้อมูล ผลขึ้นทันที ไม่ต้องเรียกผู้ช่วย'
                  : `ตรวจด้วยข้อมูล ${board.criteria.length - assistantCriteria.length} ข้อ ` +
                    `· ผู้ช่วยอ่านประวัติ ${assistantCriteria.length} ข้อ (คอลัมน์พื้นครีม)`}
              </span>
            </div>
          <div className="cbwrap">
            <table className="cbtable">
              <colgroup>
                <col className="cb-c-who" />
                {board.criteria.map((c) => (
                  <col key={c.id} />
                ))}
                <col className="cb-c-sum" />
                <col className="cb-c-act" />
              </colgroup>
              <thead>
                <tr>
                  <th>ผู้ได้รับการพิจารณา</th>
                  {board.criteria.map((c) => (
                    <th key={c.id} className={c.kind === 'assistant' ? 'is-ai' : undefined}>
                      <span className="cb-cn">
                        <b>{c.id}</b> {c.label}
                      </span>
                      <em>{c.kind === 'assistant' ? 'ผู้ช่วยอ่านประวัติ' : 'ตรวจด้วยข้อมูล'}</em>
                      {c.spec && SYNTHETIC_RULES.includes(c.spec.rule) && (
                        <em className="is-warn">⚠ วันเกิดในชุดนี้เป็นค่าที่สร้างขึ้น</em>
                      )}
                    </th>
                  ))}
                  <th>สรุปตามเกณฑ์</th>
                  <th aria-label="การจัดการ"></th>
                </tr>
              </thead>
              <tbody>
                {people.map((it) => {
                  const row = grid[it.person.name];
                  return (
                    <tr key={it.id} className={it.status === 'proposed' ? 'is-proposed' : undefined}>
                      <td>
                        <b>{it.person.name}</b>
                        <span>{leafUnit(it.person.unit)}</span>
                        {it.status === 'proposed' && (
                          <span className="cb-flag">
                            <CheckOutlined aria-hidden="true" /> ผู้ที่เสนอ
                          </span>
                        )}
                      </td>
                      {board.criteria.map((c) => {
                        const cell = row?.[c.id];
                        const v = cell?.verdict ?? 'unknown';
                        return (
                          /* ⚠ ไม่ถมสีพื้นรายช่อง — เครื่องหมายกับข้อความเป็นสีตามคำตอบ
                             ถ้าถมพื้นทุกช่อง ทั้งแถวจะกลายเป็นแถบความร้อนของ "คนคนนี้"
                             ซึ่งอ่านเป็นคะแนนรวม ทั้งที่แต่ละช่องเป็นคำตอบคนละคำถาม
                             พื้นครีมสงวนไว้บอกว่า "คอลัมน์นี้ผู้ช่วยเป็นคนอ่าน" เท่านั้น */
                          <td
                            key={c.id}
                            className={`cb-cell cb-${v}${c.kind === 'assistant' ? ' is-ai' : ''}`}
                          >
                            <span className="cb-mark">
                              {v === 'pass' ? '✓' : v === 'fail' ? '✕' : '—'}
                            </span>{' '}
                            <span className="cb-why">{cell?.detail}</span>
                          </td>
                        );
                      })}
                      <td className="cb-sum">{summariseRow(board.criteria, row)}</td>
                      <td className="cb-act">
                        {it.status === 'proposed' ? (
                          <button type="button" className="rd-link" onClick={() => unpropose(it.id)}>
                            ถอนธง
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rd-pick"
                            onClick={() => {
                              const r = propose(it.id);
                              setConflict(r.ok ? null : r.conflict);
                            }}
                          >
                            ติดธงเสนอ
                          </button>
                        )}
                        <button
                          type="button"
                          className="rd-link"
                          onClick={() => removeFromDraft(it.id)}
                        >
                          เอาออก
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* ⚠ คำอธิบายเครื่องหมายต้องอยู่ติดตาราง ไม่ใช่ย่อหน้ายาวท้ายหน้า
            และต้องอยู่ในกล่องเดียวกับข้อความกำกับ ไม่ใช่ชิปลอย ๆ
            เพราะสิ่งที่ต้องอ่านคู่กันคือ "ขีดไม่เท่ากับกากบาท" กับ "นี่ไม่ใช่คะแนน" */}
        {people.length > 0 && board.criteria.length > 0 && (
          <div className="cbnote">
            <InfoCircleOutlined aria-hidden="true" />
            <div>
              <div className="cblegend">
                <span className="cb-lg is-pass">✓ ตรงกับเกณฑ์ที่ท่านตั้ง</span>
                <span className="cb-lg is-fail">✕ ไม่ตรงกับเกณฑ์</span>
                <span className="cb-lg is-unknown">— ไม่มีข้อมูลให้ตอบ</span>
              </div>
              <p>
                ช่องสรุปเป็นการ<b>อ่านผลซ้ำตามเกณฑ์ที่ท่านตั้งเอง ไม่ใช่คะแนนความเหมาะสม</b>{' '}
                และไม่ได้เรียงลำดับผู้ใด · คอลัมน์พื้นครีมคือคอลัมน์ที่ผู้ช่วยอ่านให้ ซึ่ง
                <b>ตอบได้เฉพาะเท่าที่ประวัติในระบบมี</b> — ขีด (—) แปลว่าไม่มีข้อมูลให้ตอบ
                ไม่ได้แปลว่าท่านนั้นไม่เคยทำ
              </p>
            </div>
          </div>
        )}

        {/* ---------- ๓ ผู้ช่วยอ่านประวัติ ---------- */}
        {people.length > 0 && (
          <div className="cbai">
            <div className="cbai-hd">
              <ReadOutlined aria-hidden="true" />
              <div>
                <b>ผู้ช่วยอ่านประวัติ</b>
                <span>
                  อ่าน<b>ประวัติการรับราชการที่มีในระบบ</b>ของทุกท่านในบัญชี แล้วเล่าว่าเขียนอะไรไว้
                  และต่างกันตรงไหน — ไม่ประเมินความเหมาะสม ไม่จัดลำดับ
                  {assistantCriteria.length > 0 &&
                    ` · ตอบเกณฑ์เชิงเนื้อความ ${assistantCriteria.length} ข้อในตารางให้ด้วย`}
                </span>
              </div>
              <button
                type="button"
                className="rd-primary"
                onClick={() => run.mutate()}
                disabled={run.isPending}
              >
                {run.isPending
                  ? 'ผู้ช่วยกำลังอ่าน…'
                  : aiRan
                    ? 'อ่านใหม่อีกรอบ'
                    : 'ให้ผู้ช่วยอ่านและวิเคราะห์'}
              </button>
            </div>

            {lastRound && (
              <div className="cbround">
                <b>รอบที่ {board.rounds.length}</b>
                {/* ⚠ คุณค่าของลูปอยู่ที่ "ผลเปลี่ยนไปกี่ท่าน" ไม่ใช่ "วิเคราะห์เมื่อไร"
                    ถ้าบอกแค่เวลา ผู้ใช้ต้องจำผลรอบก่อนเอง ซึ่งไม่มีใครจำ */}
                <span>
                  {board.rounds.length < 2
                    ? `วิเคราะห์ครั้งแรกเมื่อ ${new Date(lastRound.at).toLocaleString('th-TH')}`
                    : changedCount === 0
                      ? `วิเคราะห์ใหม่แล้ว — สรุปตามเกณฑ์ของทุกท่านเหมือนรอบก่อน`
                      : `วิเคราะห์ใหม่แล้ว — สรุปตามเกณฑ์ของ ${changedCount} ท่านเปลี่ยนจากรอบก่อน`}
                </span>
                <button type="button" className="rd-link" onClick={() => setShowRounds((v) => !v)}>
                  {showRounds ? 'ซ่อนประวัติการปรับเกณฑ์' : 'ประวัติการปรับเกณฑ์'}
                </button>
              </div>
            )}

            {/* ⚠ ประวัติการปรับเกณฑ์คือสิ่งที่ทำให้การวนปรับเกณฑ์มองเห็นได้
                ถ้าเอาออกเพื่อความสวยงาม หน้านี้จะกลายเป็นเครื่องมือหาเหตุผลย้อนหลัง */}
            {showRounds && (
              <ol className="cbhist">
                {board.rounds.map((r) => (
                  <li key={r.round}>
                    <b>รอบที่ {r.round}</b>
                    <span>{new Date(r.at).toLocaleString('th-TH')}</span>
                    <span className="cb-cri">
                      {r.criteria.map((c) => `${c.id} ${c.label}`).join(' · ') || 'ไม่มีเกณฑ์'}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {aiWarnings.length > 0 && (
              <ul className="rotwarn">
                {aiWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            {run.isError && (
              <p className="state error sm">เรียกผู้ช่วยไม่สำเร็จ — {String(run.error)}</p>
            )}

            {aiRan && (
              <>
                <div className="cbcards">
                  {people.map((it) => {
                    const p = profileOf.get(it.person.name);
                    return (
                      <article
                        key={it.id}
                        className={it.status === 'proposed' ? 'cbcard is-proposed' : 'cbcard'}
                      >
                        <header>
                          <b>{it.person.name}</b>
                          <span>
                            {it.person.title} · {leafUnit(it.person.unit)}
                          </span>
                        </header>
                        <p>{p?.reading ?? 'ผู้ช่วยไม่ได้ตอบเรื่องท่านนี้'}</p>
                        {p && p.cites.length > 0 && (
                          <ul className="cbcite">
                            {p.cites.map((c) => (
                              <li key={c}>{c}</li>
                            ))}
                          </ul>
                        )}
                      </article>
                    );
                  })}
                </div>

                {aiContrasts.length > 0 && (
                  <div className="cbdiff">
                    <b>ประวัติของแต่ละท่านต่างกันตรงไหน</b>
                    <ul>
                      {aiContrasts.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="cbai-foot">
                  ข้อความทั้งหมดในส่วนนี้เป็นการ<b>อ่านประวัติที่มีในระบบ</b> ไม่ใช่การประเมินตัวบุคคล ·
                  บรรทัดที่ยกมาอ้างถูกตรวจแล้วว่าตรงกับประวัติที่ส่งให้ผู้ช่วยอ่านจริง ·
                  ขั้นจัดลำดับความเหมาะสมเป็นของคณะกรรมการตามกฎหมาย
                </p>
              </>
            )}
          </div>
        )}

        <div className="cbfoot">
          <button type="button" className="rd-ghost" onClick={onBack}>
            กลับไปเลือกต่อ
          </button>
        </div>
      </section>

      {adding && <AddCriterion posNo={posNo} onClose={() => setAdding(false)} />}
    </div>
  );
}

/* ---------------- กล่องเพิ่มเกณฑ์ ---------------- */

/**
 * ⚠ ต้องบอกก่อนกดว่าเกณฑ์ที่พิมพ์จะตรวจได้หรือไม่
 *   ปล่อยให้กดแล้วค่อยได้ขีดทั้งคอลัมน์ คือการเสียเวลาและเสียโควตาเปล่า
 *   และผู้ใช้จะสรุปว่าระบบพัง ทั้งที่ระบบตอบถูกแล้วว่าไม่มีข้อมูล
 */
function AddCriterion({ posNo, onClose }: { posNo: string; onClose: () => void }) {
  const [text, setText] = useState('');
  const blocked = looksUnanswerable(text);

  const addPreset = (label: string, spec: ComputedSpec): void => {
    addCriterion(posNo, { label, kind: 'computed', spec });
    onClose();
  };

  return (
    <div
      className="pmodal-back is-stacked"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pmodal is-narrow" role="dialog" aria-modal="true" aria-label="เพิ่มเกณฑ์">
        <header className="pmodal-hd">
          <div>
            <span className="kind">เพิ่มเกณฑ์</span>
            <b>ท่านต้องการให้ตรวจอะไรเพิ่ม</b>
          </div>
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง">
            ✕
          </button>
        </header>

        <div className="pmodal-body">
          <h4 className="cb-h">เกณฑ์ที่ระบบตรวจให้ได้เองทันที</h4>
          <p className="pmodal-note">
            กลุ่มนี้ไม่ต้องเรียกผู้ช่วย ผลขึ้นทันที ตรวจย้อนได้ว่าทำไมถึงตอบแบบนั้น และรันซ้ำกี่ครั้งก็ได้ผลเดิม
          </p>
          <div className="cbpre">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => addPreset(p.label, p.spec)}>
                <span className="cb-tag">ตรวจด้วยข้อมูล</span>
                {p.label}
              </button>
            ))}
          </div>

          <h4 className="cb-h">หรือพิมพ์เกณฑ์ของท่านเอง</h4>
          <textarea
            className="rdtext"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="เช่น เคยปฏิบัติงานสายจัดเก็บภาษีมาก่อน"
            aria-label="เกณฑ์ที่ต้องการเพิ่ม"
          />

          {text.trim() &&
            (blocked ? (
              <div className="rdcover">
                <b>เกณฑ์นี้ระบบตอบไม่ได้</b>
                <span>
                  คำว่า &ldquo;{blocked}&rdquo; ชี้ไปที่ข้อมูลที่ชุดนี้ยังเป็นค่าที่สร้างขึ้น ไม่ใช่ของจริง —
                  ถ้าเพิ่มไป จะได้ขีดทั้งคอลัมน์ทุกท่าน เพิ่มได้ถ้าต้องการบันทึกไว้ว่าเคยตั้งเกณฑ์นี้
                </span>
              </div>
            ) : (
              <div className="rdcover is-plain">
                <b>เกณฑ์นี้ต้องให้ผู้ช่วยอ่านประวัติ</b>
                <span>
                  ระบบไม่มีช่องข้อมูลที่ตอบเรื่องนี้ได้ตรง ๆ ผู้ช่วยจะอ่านประวัติการรับราชการของทุกท่านในบัญชี
                  แล้วตอบว่าใครมีช่วงที่ตรง และจะขึ้นขีดให้ท่านที่ประวัติในระบบไม่พอจะตอบ
                </span>
              </div>
            ))}
        </div>

        <div className="rdfoot">
          <span className="rd-sp" />
          <button type="button" className="rd-ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="rd-primary"
            disabled={!text.trim()}
            onClick={() => {
              addCriterion(posNo, { label: text.trim().slice(0, 200), kind: 'assistant', spec: null });
              onClose();
            }}
          >
            เพิ่มเกณฑ์
          </button>
        </div>
      </div>
    </div>
  );
}

export type { Criterion };
